import { z } from 'zod';

import { richDocSchema, storedRichDocSchema } from './rich-text';
import { slugSchema } from './store';

/**
 * Conteudos do site (RF-F1, RN-F1: Gestor de Conteudo, `content.manage`):
 * noticias, carrossel da pagina inicial, textos das paginas e paginas
 * editaveis (privacidade, termos). Ver docs/conteudos.
 *
 * Prefixos HTTP:
 *   - `/api/news/...`, `/api/content/...`  site publico;
 *   - `/api/site/content/...`              back-office (`content.manage`), so em gestao.
 *
 * Datas em ISO-8601 (UTC); o site e o back-office mostram-nas em hora de Lisboa.
 */

const datetimeSchema = z.string().datetime({ offset: true });

/** Uma imagem carregada (`/api/media/...`); nada de imagens de terceiros. */
const mediaSrcSchema = z
  .string()
  .trim()
  .regex(/^\/api\/media\/[0-9a-f-]{36}\/(sm|md|lg)\.webp$/, 'carregue a imagem no back-office');

/** Ligacao de um botao: um caminho do site (`/loja`) ou `https://...`. */
export const buttonHrefSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => /^\/(?![/\\])/.test(v) || /^https:\/\/[^\s]+$/.test(v), '/caminho ou https://...');

// ---------------------------------------------------------------------------
// Categorias
// ---------------------------------------------------------------------------

export const postCategorySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  /** Noticias publicadas nesta categoria (so no back-office conta tudo). */
  posts: z.number().int(),
});
export type PostCategory = z.infer<typeof postCategorySchema>;

export const postCategoryInputSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(60),
  sortOrder: z.number().int().min(0).max(1000).default(0),
});
export type PostCategoryInput = z.infer<typeof postCategoryInputSchema>;

// ---------------------------------------------------------------------------
// Noticias
// ---------------------------------------------------------------------------

/**
 * - `draft`: so no back-office;
 * - `published`: no site a partir de `publishAt` (antes disso, "agendada");
 * - `archived`: saiu do site, mas fica no back-office.
 */
export const postStatusSchema = z.enum(['draft', 'published', 'archived']);
export type PostStatus = z.infer<typeof postStatusSchema>;

const coverSchema = z.object({ url: z.string(), alt: z.string() }).nullable();

/** O que aparece nas listas (site e pagina inicial). */
export const publicPostSummarySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  cover: coverSchema,
  category: z.object({ slug: z.string(), name: z.string() }).nullable(),
  publishedAt: z.string(),
  readingMinutes: z.number().int(),
});
export type PublicPostSummary = z.infer<typeof publicPostSummarySchema>;

export const publicPostSchema = publicPostSummarySchema.extend({
  body: storedRichDocSchema,
  updatedAt: z.string(),
});
export type PublicPost = z.infer<typeof publicPostSchema>;

export const NEWS_PAGE_SIZE = 12;

export const newsListQuerySchema = z.object({
  categoria: slugSchema.optional(),
  pagina: z.coerce.number().int().min(1).max(1000).default(1),
  /** Pagina inicial: so as ultimas N. */
  limite: z.coerce.number().int().min(1).max(NEWS_PAGE_SIZE).optional(),
});

export const newsPageSchema = z.object({
  items: z.array(publicPostSummarySchema),
  page: z.number().int(),
  pages: z.number().int(),
  total: z.number().int(),
});
export type NewsPage = z.infer<typeof newsPageSchema>;

export const postInputSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1).max(140),
    /** Uma ou duas frases: lista, pagina inicial e pre-visualizacoes de links. */
    summary: z.string().trim().max(300).default(''),
    body: richDocSchema,
    coverUrl: mediaSrcSchema.nullable().default(null),
    coverAlt: z.string().trim().max(300).default(''),
    categoryId: z.string().uuid().nullable().default(null),
  })
  .refine((p) => p.coverUrl === null || p.coverAlt !== '', {
    message: 'descreva a imagem (para quem nao a ve)',
    path: ['coverAlt'],
  });
export type PostInput = z.input<typeof postInputSchema>;

export const publishPostRequestSchema = z.object({
  /** `null` = agora. Uma data futura agenda a noticia. */
  publishAt: datetimeSchema.nullable().default(null),
});

export const adminPostSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  body: storedRichDocSchema,
  coverUrl: z.string().nullable(),
  coverAlt: z.string(),
  categoryId: z.string().uuid().nullable(),
  status: postStatusSchema,
  /** Quando foi (ou vai ser) publicada. */
  publishAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Login de quem escreveu e de quem mexeu por ultimo. */
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type AdminPost = z.infer<typeof adminPostSchema>;

/** A lista do back-office: tudo menos o corpo. */
export const adminPostSummarySchema = adminPostSchema.omit({ body: true });
export type AdminPostSummary = z.infer<typeof adminPostSummarySchema>;

/** Estado como o staff o ve: uma publicada com data futura esta agendada. */
export type PostState = 'draft' | 'scheduled' | 'published' | 'archived';

export function postState(
  post: { status: PostStatus; publishAt: string | null },
  now = new Date(),
): PostState {
  if (post.status !== 'published') return post.status;
  return post.publishAt !== null && new Date(post.publishAt) > now ? 'scheduled' : 'published';
}

// ---------------------------------------------------------------------------
// Carrossel
// ---------------------------------------------------------------------------

/** Por agora so ha o da pagina inicial; o modelo aceita outros mais tarde. */
export const carouselNameSchema = z.enum(['inicio']);
export type CarouselName = z.infer<typeof carouselNameSchema>;

export const publicSlideSchema = z.object({
  id: z.string().uuid(),
  imageUrl: z.string(),
  /** Recorte para telemovel (mais alto); sem ele usa-se a mesma imagem. */
  mobileImageUrl: z.string().nullable(),
  alt: z.string(),
  title: z.string(),
  text: z.string(),
  button: z.object({ label: z.string(), href: z.string() }).nullable(),
});
export type PublicSlide = z.infer<typeof publicSlideSchema>;

export const slideInputSchema = z
  .object({
    imageUrl: mediaSrcSchema,
    mobileImageUrl: mediaSrcSchema.nullable().default(null),
    alt: z.string().trim().min(1, 'descreva a imagem (para quem nao a ve)').max(300),
    title: z.string().trim().max(80).default(''),
    text: z.string().trim().max(200).default(''),
    buttonLabel: z.string().trim().max(30).default(''),
    buttonHref: z.union([buttonHrefSchema, z.literal('')]).default(''),
    active: z.boolean().default(true),
    /** `null` = ja; `endsAt` `null` = sem fim. */
    startsAt: datetimeSchema.nullable().default(null),
    endsAt: datetimeSchema.nullable().default(null),
  })
  .refine((s) => (s.buttonLabel === '') === (s.buttonHref === ''), {
    message: 'o botao precisa de texto e de ligacao',
    path: ['buttonHref'],
  })
  .refine(
    (s) => s.startsAt === null || s.endsAt === null || new Date(s.endsAt) > new Date(s.startsAt),
    {
      message: 'o fim tem de ser depois do inicio',
      path: ['endsAt'],
    },
  );
export type SlideInput = z.input<typeof slideInputSchema>;

export const adminSlideSchema = z.object({
  id: z.string().uuid(),
  carousel: carouselNameSchema,
  imageUrl: z.string(),
  mobileImageUrl: z.string().nullable(),
  alt: z.string(),
  title: z.string(),
  text: z.string(),
  buttonLabel: z.string(),
  buttonHref: z.string(),
  active: z.boolean(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  sortOrder: z.number().int(),
  /** Se aparece no site agora (ligado e dentro das datas). */
  live: z.boolean(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});
export type AdminSlide = z.infer<typeof adminSlideSchema>;

export const slideOrderRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
});

/** Slides que se mostram no maximo. */
export const CAROUSEL_MAX_SLIDES = 8;

// ---------------------------------------------------------------------------
// Textos das paginas
// ---------------------------------------------------------------------------

export interface SiteTextDef {
  key: string;
  /** Onde aparece, para o back-office agrupar. */
  page: 'Início' | 'Sobre nós' | 'Memorial' | 'Parceiros';
  label: string;
  /** O texto de origem (no codigo); volta a ele quem carregar em "Repor". */
  defaultText: string;
  max: number;
  /** Paragrafos separados por uma linha em branco. */
  multiline?: boolean;
}

const t = (
  key: string,
  page: SiteTextDef['page'],
  label: string,
  max: number,
  defaultText: string,
  multiline = false,
): SiteTextDef => ({ key, page, label, max, defaultText, multiline });

/**
 * Os textos das paginas que o Gestor de Conteudo pode mudar sem mexer no
 * codigo. A disposicao (blocos, botoes, cores) fica no codigo; o texto vem
 * daqui, com o que estiver gravado por cima. Uma chave nova entra aqui e no
 * componente que a usa; tirar uma chave deixa o texto gravado esquecido.
 */
export const SITE_TEXTS: readonly SiteTextDef[] = [
  t('inicio.hero.etiqueta', 'Início', 'Topo: etiqueta', 40, 'Desde 2002'),
  t('inicio.hero.titulo', 'Início', 'Topo: título', 80, "Let's Grow uP together"),
  t(
    'inicio.hero.texto',
    'Início',
    'Topo: texto',
    300,
    'Somos uma associação de esports sem fins lucrativos. Crescemos a gostar de videojogos e estamos cá para partilhar essa paixão.',
  ),
  t('inicio.fazemos.titulo', 'Início', 'O que fazemos: título', 80, 'Mais do que jogar'),
  t('inicio.fazemos.1.titulo', 'Início', 'O que fazemos 01: título', 40, 'Comunidade'),
  t(
    'inicio.fazemos.1.texto',
    'Início',
    'O que fazemos 01: texto',
    300,
    'Sócios, equipas e jogadores de todas as idades. Torneios, encontros e um Discord onde há sempre alguém para jogar.',
  ),
  t('inicio.fazemos.2.titulo', 'Início', 'O que fazemos 02: título', 40, 'Arena'),
  t(
    'inicio.fazemos.2.texto',
    'Início',
    'O que fazemos 02: texto',
    300,
    'Um espaço para jovens jogarem com regras claras, acompanhados por monitores, e com os pais a par de tudo.',
  ),
  t('inicio.fazemos.3.titulo', 'Início', 'O que fazemos 03: título', 40, 'Escolas e eventos'),
  t(
    'inicio.fazemos.3.texto',
    'Início',
    'O que fazemos 03: texto',
    300,
    'Competições InterEscolas, formação e eventos onde os videojogos servem para aprender e crescer.',
  ),
  t('inicio.arena.titulo', 'Início', 'Arena: título', 80, 'Jogar com regras e com quem cuida'),
  t(
    'inicio.arena.texto',
    'Início',
    'Arena: texto',
    300,
    'Hora e meia seguida no máximo, pausa obrigatória, três horas por dia. Os pais acompanham as sessões no site e recebem um resumo por email todas as semanas.',
  ),
  t('inicio.socios.titulo', 'Início', 'Sócios: título', 80, 'Faz parte da Grow uP'),
  t(
    'inicio.socios.texto',
    'Início',
    'Sócios: texto',
    300,
    'Cria a tua conta, pede para seres membro e, se quiseres, torna-te associado: desconto na loja, mais tempo na Arena e prioridade nos eventos.',
  ),
  t('sobre.titulo', 'Sobre nós', 'Topo: título', 80, 'Crescemos a jogar'),
  t(
    'sobre.texto',
    'Sobre nós',
    'Topo: texto',
    400,
    'A Grow uP é uma associação de esports sem fins lucrativos, ativa desde 2002. Crescemos a gostar de videojogos e estamos cá para partilhar essa paixão.',
  ),
  t('sobre.ideia.titulo', 'Sobre nós', 'A ideia: título', 80, 'Os videojogos ensinam'),
  t(
    'sobre.ideia.texto',
    'Sobre nós',
    'A ideia: texto',
    1500,
    'Os videojogos e os esports podem ser muito mais do que entretenimento. Bem acompanhados, ensinam a trabalhar em equipa, a gerir o tempo, a perder e a ganhar.\n\nPor isso fazemos as coisas com regras: limites de tempo na Arena, pais informados, e um ambiente onde quem joga é tratado com respeito, seja qual for o nível.',
    true,
  ),
  t('sobre.fazemos.titulo', 'Sobre nós', 'O que fazemos: título', 80, 'Onde nos encontras'),
  t('sobre.fazemos.1.titulo', 'Sobre nós', 'O que fazemos 1: título', 40, 'Arena'),
  t(
    'sobre.fazemos.1.texto',
    'Sobre nós',
    'O que fazemos 1: texto',
    300,
    'Na Casa da Juventude das Tapadas das Mercês, um espaço onde os jovens jogam com tempo limitado, pausas e monitores por perto.',
  ),
  t('sobre.fazemos.2.titulo', 'Sobre nós', 'O que fazemos 2: título', 40, 'Escolas'),
  t(
    'sobre.fazemos.2.texto',
    'Sobre nós',
    'O que fazemos 2: texto',
    300,
    'As competições InterEscolas em Sintra e projetos como o Build to Learn in Gaming levam os esports para dentro da escola, como ferramenta para aprender.',
  ),
  t('sobre.fazemos.3.titulo', 'Sobre nós', 'O que fazemos 3: título', 40, 'Equipas e competição'),
  t(
    'sobre.fazemos.3.texto',
    'Sobre nós',
    'O que fazemos 3: texto',
    300,
    'Equipas de esports e de jogos de cartas (Pokémon TCG, Lorcana) que competem em Portugal e lá fora.',
  ),
  t('sobre.fazemos.4.titulo', 'Sobre nós', 'O que fazemos 4: título', 40, 'Eventos'),
  t(
    'sobre.fazemos.4.texto',
    'Sobre nós',
    'O que fazemos 4: texto',
    300,
    'Torneios, encontros da comunidade e presença nos grandes eventos do setor, com voluntários e streamers da casa.',
  ),
  t('sobre.fazemos.5.titulo', 'Sobre nós', 'O que fazemos 5: título', 40, 'Streamers'),
  t(
    'sobre.fazemos.5.texto',
    'Sobre nós',
    'O que fazemos 5: texto',
    300,
    'Uma equipa de criadores de conteúdo que transmite os nossos eventos e faz crescer a comunidade.',
  ),
  t('sobre.junta.titulo', 'Sobre nós', 'Junta-te: título', 80, 'Há lugar para ti'),
  t(
    'sobre.junta.texto',
    'Sobre nós',
    'Junta-te: texto',
    300,
    'Como sócio, voluntário num evento, streamer ou parceiro. Segue-nos ou fala connosco.',
  ),
  // Sem texto de origem: o memorial comeca vazio e quem o gere escreve-o.
  t('memorial.texto', 'Memorial', 'Texto de abertura', 1500, '', true),
  t('parceiros.titulo', 'Parceiros', 'Título', 80, 'Parceiros'),
  t(
    'parceiros.texto',
    'Parceiros',
    'Texto de abertura',
    400,
    'Desde 2002 que a Grow uP conta com instituições e marcas que acreditam nos esports como escola. Obrigado a quem cresce connosco.',
  ),
  t(
    'parceiros.convite.titulo',
    'Parceiros',
    'Convite: título',
    80,
    'Quer ser parceiro da Grow uP?',
  ),
  t(
    'parceiros.convite.texto',
    'Parceiros',
    'Convite: texto',
    300,
    'Apoiamos jovens jogadores, equipas e eventos desde 2002. Fale connosco e encontramos a parceria certa.',
  ),
];

export type SiteTextKey = string;

export const siteTextByKey = (key: string): SiteTextDef | undefined =>
  SITE_TEXTS.find((d) => d.key === key);

/** `GET /api/content/texts`: so os textos mudados no back-office (chave -> texto). */
export const siteTextOverridesSchema = z.record(z.string(), z.string());
export type SiteTextOverrides = z.infer<typeof siteTextOverridesSchema>;

export const adminSiteTextSchema = z.object({
  key: z.string(),
  /** O texto que o site mostra agora. */
  text: z.string(),
  /** Mudado no back-office (senao e o de origem). */
  edited: z.boolean(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type AdminSiteText = z.infer<typeof adminSiteTextSchema>;

/** `null` = repor o texto de origem. */
export const siteTextInputSchema = z.object({
  text: z.string().max(5000).nullable(),
});

// ---------------------------------------------------------------------------
// Paginas editaveis
// ---------------------------------------------------------------------------

export const pageKeySchema = z.enum(['privacidade', 'termos']);
export type PageKey = z.infer<typeof pageKeySchema>;

export const PAGE_LABELS: Record<PageKey, { title: string; path: string }> = {
  privacidade: { title: 'Política de privacidade', path: '/privacidade' },
  termos: { title: 'Termos de utilização', path: '/termos' },
};

export const publicPageSchema = z.object({
  key: pageKeySchema,
  title: z.string(),
  body: storedRichDocSchema,
  updatedAt: z.string(),
});
export type PublicPage = z.infer<typeof publicPageSchema>;

export const pageInputSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: richDocSchema,
});
export type PageInput = z.input<typeof pageInputSchema>;

export const adminPageSchema = publicPageSchema.extend({
  updatedBy: z.string(),
});
export type AdminPage = z.infer<typeof adminPageSchema>;

/** Uma versao anterior de um texto ou de uma pagina. */
export const contentRevisionSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  createdBy: z.string(),
  /** Texto: `{ text }`; pagina: `{ title, body }`. */
  content: z.unknown(),
});
export type ContentRevision = z.infer<typeof contentRevisionSchema>;
